<script lang="ts" setup>
import { computed, ref, unref } from 'vue'
import { useRouter } from 'vue-router'
import { layoutConfig } from '@layouts'
import { can } from '@layouts/plugins/casl'
import { useLayoutConfigStore } from '@layouts/stores/config'
import type { NavLink } from '@layouts/types'
import { getComputedNavLinkToProp, getDynamicI18nProps, isNavLinkActive } from '@layouts/utils'
import { usePwa } from '@/composables/usePwa'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'

const props = defineProps<{
  item: NavLink
}>()

const router = useRouter()
const { triggerInstall, canInstall, openPwa } = usePwa()
const onboardingStore = useOnboardingStore()

const configStore = useLayoutConfigStore()
const hideTitleAndBadge = configStore.isVerticalNavMini()

const formattedBadgeContent = computed(() => {
  if (!props.item.badgeContent) return undefined
  const raw = String(props.item.badgeContent).trim()
  const num = Number(raw)
  if (!Number.isNaN(num) && num > 99) {
    return '99+'
  }
  return raw
})

const isOnboardingTarget = computed(() => {
  if (!props.item.settingsAnchor || !onboardingStore.isActive) return false
  return onboardingStore.currentStep?.anchors?.includes(props.item.settingsAnchor) ?? false
})

const handleInstallClick = async (e: Event) => {
  if (props.item.action !== 'install-pwa') return

  e.preventDefault()

  if (canInstall.value) {
    await triggerInstall()
  } else {
    openPwa()
  }
}

const handleSettingsClick = () => {
  if (props.item.settingsTo) {
    router.push(props.item.settingsTo)
  } else if (props.item.settingsAction) {
    props.item.settingsAction()
  }
}

const handleSubmenuClick = (sub: { to?: any; action?: () => void }) => {
  if (sub.to) {
    router.push(sub.to)
  } else if (sub.action) {
    sub.action()
  }
}

const isMenuOpen = ref(false)
let menuCloseTimer: ReturnType<typeof setTimeout> | null = null

const startMenuCloseTimer = () => {
  if (!isMenuOpen.value) return
  if (menuCloseTimer) clearTimeout(menuCloseTimer)
  menuCloseTimer = setTimeout(() => {
    isMenuOpen.value = false
  }, 300)
}

const cancelMenuCloseTimer = () => {
  if (menuCloseTimer) {
    clearTimeout(menuCloseTimer)
    menuCloseTimer = null
  }
}
</script>

<template>
  <li
    v-if="can(item.action, item.subject)"
    class="nav-link"
    :class="{ disabled: item.disable }"
    @mouseenter="cancelMenuCloseTimer"
    @mouseleave="startMenuCloseTimer"
  >
    <Component
      :is="item.to ? 'RouterLink' : 'button'"
      v-bind="getComputedNavLinkToProp(item)"
      :class="{ 'router-link-active router-link-exact-active': isNavLinkActive(item, router) }"
      @click="handleInstallClick"
    >
      <div class="nav-item-icon-box">
        <Component
          :is="layoutConfig.app.iconRenderer || 'div'"
          v-bind="((item.icon || layoutConfig.verticalNav.defaultNavItemIconProps) as Record<string, any>)"
          class="nav-item-icon"
        />
        <!-- 👉 Bubble over icon (Bitrix24 style) -->
        <span
          v-if="formattedBadgeContent"
          class="nav-item-badge-bubble"
          :class="item.badgeClass"
        >
          {{ formattedBadgeContent }}
        </span>
      </div>

      <TransitionGroup name="transition-slide-x">
        <!-- 👉 Title -->
        <Component
          :is="layoutConfig.app.i18n.enable ? 'i18n-t' : 'span'"
          v-show="!hideTitleAndBadge"
          key="title"
          class="nav-item-title"
          v-bind="getDynamicI18nProps(item.title, 'span')"
        >
          {{ item.title }}
        </Component>
      </TransitionGroup>
    </Component>

    <!-- 👉 Settings gear button (Bitrix24 style) as sibling to link -->
    <div
      v-if="item.hasSettings"
      v-show="!hideTitleAndBadge"
      class="nav-item-settings-wrapper"
    >
      <template v-if="item.settingsMenu?.length">
        <VMenu
          v-model="isMenuOpen"
          location="end"
          offset="8"
          :close-on-content-click="true"
        >
          <template #activator="{ props: menuProps }">
            <VIconBtn
              v-bind="menuProps"
              :ripple="false"
              class="nav-item-settings-btn"
              :class="{ 'is-onboarding-target': isOnboardingTarget, 'is-menu-open': isMenuOpen }"
              :data-onboarding="item.settingsAnchor"
              size="small"
              icon="ri-settings-4-line"
            />
          </template>
          <VList
            min-width="200"
            density="compact"
            class="nav-settings-menu"
            @mouseenter="cancelMenuCloseTimer"
            @mouseleave="startMenuCloseTimer"
          >
            <VListItem
              v-for="(sub, idx) in item.settingsMenu"
              :key="idx"
              :title="sub.title"
              @click="handleSubmenuClick(sub)"
            />
          </VList>
        </VMenu>
      </template>
      <template v-else-if="item.settingsTo || item.settingsAction">
        <VIconBtn
          :ripple="false"
          class="nav-item-settings-btn"
          :class="{ 'is-onboarding-target': isOnboardingTarget }"
          :data-onboarding="item.settingsAnchor"
          size="small"
          icon="ri-settings-4-line"
          @click="handleSettingsClick"
        />
      </template>
    </div>
  </li>
</template>

<style lang="scss">
.layout-vertical-nav {
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));

  .nav-link {
    position: relative;
    display: flex;
    align-items: center;
  }

  .nav-link a,
  .nav-link button {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .nav-item-icon-box {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .nav-item-badge-bubble {
    position: absolute;
    top: -4px;
    left: calc(100% - 5px);
    transform: translateX(-50%);
    height: 14px;
    min-width: 14px;
    padding: 0 4px;
    border-radius: 14px;
    background-color: #f2473d;
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    line-height: 14px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    z-index: 2;
    pointer-events: none;
  }

  .nav-item-settings-wrapper {
    position: absolute;
    right: 6px;
    top: 19px;
    transform: translateY(-50%);
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    z-index: 3;
  }

  .nav-item-settings-btn {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease 0s;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    width: 24px !important;
    height: 24px !important;
    padding: 0 !important;
    margin: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;

    // Убираем фоновый круг и оверлей Vuetify
    &::before,
    &::after,
    .v-icon-btn__overlay,
    .v-icon-btn__underlay {
      display: none !important;
      opacity: 0 !important;
      background: transparent !important;
    }

    .v-icon-btn__content {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      height: 100% !important;
    }

    &:hover {
      background: transparent !important;
      color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
    }

    &.is-onboarding-target,
    &.is-menu-open {
      opacity: 1 !important;
      pointer-events: auto !important;
      transition: none !important;
    }
  }

  .nav-link:hover .nav-item-settings-btn {
    opacity: 1;
    pointer-events: auto;
    transition: opacity 0.2s ease 1s;
  }
}

.nav-link button {
  all: unset;
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
}
</style>
